import type { Observable } from 'rxjs';
import type { DataSet } from '../../../../../graph/dataset';
import type { Profile } from '../../../../../profile';
import type {
    GraphTimeWindow,
    RenderContext,
    RenderContextKind,
    RenderRuntimeGraphSettings,
} from '../../elements/types';
import type { SupportedSwarmTypes } from '../../swarmCompatibility';
import type { HorizontalAlignment, TableLayoutShape } from '../../elements/table/contracts';
import type {
    RuntimeDataServiceId,
    RuntimeDataServiceInput,
    RuntimeDataServiceOutput,
} from './contracts';
import type { RUNTIME_DATA_SERVICE_CLASSES } from './serviceDefinitions';

interface RuntimeDataServiceDependencies {
    requestData$<TServiceId extends RuntimeDataServiceId>(
        serviceId: TServiceId,
        input: RuntimeDataServiceInput<TServiceId>
    ): Observable<RuntimeDataServiceOutput<TServiceId>>;
}

export interface RuntimeTabularCellRenderOptions {
    // `profile` comes from the render runtime, and `namingConventions` / `units` are always
    // resolvable from it plus the DataSet, so all three are required and need no `require*`
    // guard at the use site. `timezone` is only resolved for columns that render times.
    // See `requireTimezone`.
    profile: Profile;
    namingConventions: Record<string, string>;
    /** Resolved unit per data-type key, keyed as `getDataType` keys it. `null` means unitless. */
    units: Record<string, string | null>;
    timezone?: string;
}

// Options are still passed to `resolveCell` because some text cells compose their string
// from render context — for example a comment row rendering "<timestamp> - <username>".
// Measurement columns never need it: the framework converts and rounds for them.
type RuntimeTabularResolveCellValue<TEntry, TValue> = {
    bivarianceHack: (entry: TEntry, options: RuntimeTabularCellRenderOptions) => TValue;
}['bivarianceHack'];

export type RuntimeBindingArgPolicy = 'optional' | 'required' | 'forbidden';

export interface TimeRange {
    from: number;
    to: number;
}

interface RuntimeTabularColumnBase {
    key: string;
    description?: string;
    defaultAlignment?: HorizontalAlignment;
}

/**
 * A measured quantity. Declares only *which* quantity it is; the framework derives the name,
 * unit and rounding from `getDataType(dataType)` and converts before rounding, so a value
 * cannot drift from its unit label. `resolveCell` must return the canonical millimetre value.
 */
interface RuntimeTabularMeasurementColumn<TEntry> extends RuntimeTabularColumnBase {
    kind: 'measurement';
    /** A `getDataType` key, e.g. `DATA_TYPE.VTOP` or `'vmax'`. */
    dataType: string;
    /**
     * Overrides the derived name for values that are measured in a data type's unit but are
     * not that quantity — an alarm trigger level in mm/s, say, which is not a Vtop reading.
     * The unit is still derived and appended, so this label must not contain one.
     */
    label?: string;
    /**
     * Distinguishes several columns of the same quantity, typically an axis. Rendered between
     * the derived name and the unit, e.g. `qualifier: 'x'` gives `Vtop x (mm/s)` — matching
     * how the graph tooltip and the VPER table already compose axis headers.
     */
    qualifier?: string;
    /**
     * Set when the backend already converted this value to the profile's distance unit, so it
     * only rounds. Marks a server-side conversion that should move to the client: see #9972.
     */
    preConverted?: true;
    resolveCell: RuntimeTabularResolveCellValue<TEntry, number | null | undefined>;
}

/**
 * A start/end millisecond timestamp pair, formatted as a range in the DataSet timezone.
 * `resolveCell` returns the two raw timestamps rather than a formatted string, so the range
 * format stays a framework decision instead of a per-service one.
 */
interface RuntimeTabularTimeRangeColumn<TEntry> extends RuntimeTabularColumnBase {
    kind: 'timeRange';
    label: string;
    resolveCell: RuntimeTabularResolveCellValue<TEntry, TimeRange | null | undefined>;
}

/** A millisecond timestamp, formatted in the DataSet timezone. */
interface RuntimeTabularTimestampColumn<TEntry> extends RuntimeTabularColumnBase {
    kind: 'timestamp';
    label: string;
    resolveCell: RuntimeTabularResolveCellValue<TEntry, number | null | undefined>;
}

/**
 * Free text, rendered as-is. Avoid annotating `options` as `RuntimeTabularCellRenderOptions`:
 * that broader type bypasses the timezone restriction for plain text.
 */
type RuntimeTabularTextColumn<TEntry> = RuntimeTabularColumnBase & {
    kind: 'text';
    label: string;
} & (
        | {
              requiresTimezone?: never;
              resolveCell: (
                  entry: TEntry,
                  options: Omit<RuntimeTabularCellRenderOptions, 'timezone'>
              ) => string | null;
          }
        | {
              requiresTimezone: true;
              resolveCell: (
                  entry: TEntry,
                  options: RuntimeTabularCellRenderOptions & { timezone: string }
              ) => string | null;
          }
    );

/**
 * A unitless integer such as an exceedance count. Deliberately distinct from `measurement`
 * so it is never routed through `amountAfterDot` or distance conversion.
 */
interface RuntimeTabularCountColumn<TEntry> extends RuntimeTabularColumnBase {
    kind: 'count';
    label: string;
    resolveCell: RuntimeTabularResolveCellValue<TEntry, number | null | undefined>;
}

export type RuntimeTabularColumnDescriptor<TEntry = unknown> =
    | RuntimeTabularMeasurementColumn<TEntry>
    | RuntimeTabularTimeRangeColumn<TEntry>
    | RuntimeTabularTimestampColumn<TEntry>
    | RuntimeTabularTextColumn<TEntry>
    | RuntimeTabularCountColumn<TEntry>;

// A plain `Omit` over a discriminated union collapses it into one object type and loses the
// `kind` discriminant, so distribute it across the members instead.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** A column without its `resolveCell`, for the helpers that only read a column's metadata. */
export type RuntimeTabularColumnMetadata = DistributiveOmit<
    RuntimeTabularColumnDescriptor,
    'resolveCell'
>;

export interface RuntimeBindingInputArgs {
    context?: RenderContext;
    graphTimeWindow: GraphTimeWindow | undefined;
    graphSettings: RenderRuntimeGraphSettings | undefined;
    arg: string | null;
    templateId: string | null;
}

export interface RuntimeReactiveBindingInputArgs extends RuntimeBindingInputArgs {
    getDataSetContext: () => {
        dataSet: DataSet;
        dataSetRequestError$: Observable<unknown>;
    };
}

type RuntimeResultLinkDescriptorBase<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId
> = {
    key: string;
    label: string;
    description: string;
    argPolicy: RuntimeBindingArgPolicy;
    argOptions?: ReadonlyArray<readonly [string, string]>;
    exposeInInsertionPresets: boolean;
    // Enforced by the data service registry before the input builder runs.
    requiresGraphTimeWindow?: boolean;
    supportedContextKinds: readonly RenderContextKind[];
    supportedSwarmTypes: SupportedSwarmTypes;
    path: string;
    valueKind: 'tabular' | 'scalar' | 'image' | 'image-list';
} & (
    | {
          buildInput: (args: RuntimeBindingInputArgs) => RuntimeDataServiceInput<TServiceId>;
          resolveBuildInput$?: never;
      }
    | {
          buildInput?: never;
          // Runs on subscription; DataSet access stays lazy.
          resolveBuildInput$: (
              args: RuntimeReactiveBindingInputArgs
          ) => Observable<RuntimeDataServiceInput<TServiceId>>;
      }
);

export interface CellRenderOptionsDeps {
    getDataSet: () => DataSet;
}

export type RuntimeResultLinkDescriptorTabular<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId,
    TRow = never
> = RuntimeResultLinkDescriptorBase<TServiceId> & {
    valueKind: 'tabular';
    columns: readonly RuntimeTabularColumnDescriptor<TRow>[];
    parseTableRows: (linkValue: unknown) => readonly unknown[];
    buildTableShape?: (
        settings: TableLayoutShape,
        linkValue: unknown,
        context: RenderContext,
        options: RuntimeTabularCellRenderOptions
    ) => TableLayoutShape;
};

type RuntimeResultLinkDescriptorScalar<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId
> = RuntimeResultLinkDescriptorBase<TServiceId> & {
    valueKind: 'scalar';
    /**
     * Localized, markdown-free text rendered for a null value in the editor and preview.
     * Export deliberately remains empty, and scalar links without this opt-in keep their
     * unresolved placeholder so template authors can still see the binding.
     */
    nullPreviewFallback?: string;
};

type RuntimeResultLinkDescriptorImage<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId
> = RuntimeResultLinkDescriptorBase<TServiceId> & {
    valueKind: 'image';
};

type RuntimeResultLinkDescriptorImageList<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId
> = RuntimeResultLinkDescriptorBase<TServiceId> & {
    valueKind: 'image-list';
};

export type RuntimeResultLinkDescriptor<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId,
    TRow = never
> =
    | RuntimeResultLinkDescriptorTabular<TServiceId, TRow>
    | RuntimeResultLinkDescriptorScalar<TServiceId>
    | RuntimeResultLinkDescriptorImage<TServiceId>
    | RuntimeResultLinkDescriptorImageList<TServiceId>;

export interface RuntimeDataServiceRequest<TServiceId extends RuntimeDataServiceId> {
    input: RuntimeDataServiceInput<TServiceId>;
    deps: RuntimeDataServiceDependencies;
}

type RuntimeDataServiceClassRegistry = typeof RUNTIME_DATA_SERVICE_CLASSES;

type RuntimeDataServiceClassFromRegistry = RuntimeDataServiceClassRegistry[number];

type RuntimeDataServiceClassById = {
    [TClass in RuntimeDataServiceClassFromRegistry as TClass['serviceId']]: TClass;
};

type RuntimeDataServiceClassForId<TServiceId extends RuntimeDataServiceId> =
    TServiceId extends keyof RuntimeDataServiceClassById
        ? RuntimeDataServiceClassById[TServiceId]
        : never;

export type RuntimeDataServiceLinkKey<TServiceId extends RuntimeDataServiceId> =
    RuntimeDataServiceClassForId<TServiceId>['serviceResultLinks'][number]['key'];

type RuntimeServiceResultLink<TServiceId extends RuntimeDataServiceId> =
    RuntimeDataServiceClassForId<TServiceId>['serviceResultLinks'][number] &
        RuntimeResultLinkDescriptor<TServiceId>;

export type BindingId = `${RuntimeDataServiceId}.${string}`;
export type BindingInstanceKey = string;

export interface ActiveDataBinding<TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId> {
    serviceId: TServiceId;
    linkKey: RuntimeDataServiceLinkKey<TServiceId>;
    arg?: string | null;
}

export interface ActiveBindingObserverConfig {
    elementPath: string;
}

export interface RuntimeBindingResolveContext {
    getDataSetContext?: RuntimeReactiveBindingInputArgs['getDataSetContext'];
    context?: RenderContext;
    // Populated by the render runtime from its graph settings; callers only pass it
    // to override the report-level window.
    graphTimeWindow?: GraphTimeWindow;
    graphSettings?: RenderRuntimeGraphSettings;
    templateId?: string | null;
}

interface DebugServiceCatalogLinkItem {
    key: string;
    label: string;
    description: string;
    path: string;
    valueKind: RuntimeResultLinkDescriptor['valueKind'];
    supportedContextKinds: readonly RenderContextKind[];
    supportedSwarmTypes: SupportedSwarmTypes;
}

export interface DebugServiceCatalogItem {
    serviceId: RuntimeDataServiceId;
    label: string;
    description: string;
    links: readonly DebugServiceCatalogLinkItem[];
}

interface DebugActiveBindingInputBucket {
    inputKey: string;
    input: RuntimeDataServiceInput<RuntimeDataServiceId>;
    observerCount: number;
    observers: readonly ActiveBindingObserverConfig[];
}

export interface DebugActiveBindingDetails {
    bindingInstanceKey: BindingInstanceKey;
    bindingId: BindingId;
    binding: ActiveDataBinding<RuntimeDataServiceId>;
    inputBuckets: readonly DebugActiveBindingInputBucket[];
}

export interface RenderRuntimeDataServicesDebugApi {
    listCatalog(): readonly DebugServiceCatalogItem[];
    observeActiveBindingInstanceKeys(): Observable<readonly BindingInstanceKey[]>;
    observeActiveBindingDebug(
        bindingInstanceKey: BindingInstanceKey
    ): Observable<DebugActiveBindingDetails>;
    observeActiveBindingInput(
        bindingInstanceKey: BindingInstanceKey,
        inputKey: string
    ): Observable<unknown>;
}

interface RuntimeDataServiceMetadata {
    label: string;
    description: string;
}

export interface RuntimeDataServiceClass<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId
> {
    readonly serviceId: TServiceId;
    readonly serviceMetadata: RuntimeDataServiceMetadata;
    readonly serviceResultLinks: readonly RuntimeResultLinkDescriptor<TServiceId>[];
}

export abstract class RuntimeDataService<
    TServiceId extends RuntimeDataServiceId = RuntimeDataServiceId
> {
    // TypeScript does not infer static subclass fields from `this.constructor`
    // strongly enough here, so we centralize the casts instead of repeating
    // per-service instance aliases for id/metadata/resultLinks.
    private get constructorMetadata(): RuntimeDataServiceClass<TServiceId> {
        return this.constructor as unknown as RuntimeDataServiceClass<TServiceId>;
    }

    get id(): TServiceId {
        return this.constructorMetadata.serviceId;
    }

    get metadata(): RuntimeDataServiceMetadata {
        return this.constructorMetadata.serviceMetadata;
    }

    get resultLinks(): readonly RuntimeServiceResultLink<TServiceId>[] {
        return this.constructorMetadata
            .serviceResultLinks as readonly RuntimeServiceResultLink<TServiceId>[];
    }

    abstract validateArgs(args: unknown): RuntimeDataServiceInput<TServiceId>;

    abstract validateResult(result: unknown): RuntimeDataServiceOutput<TServiceId>;

    abstract createResult$(
        request: RuntimeDataServiceRequest<TServiceId>
    ): Observable<RuntimeDataServiceOutput<TServiceId>>;
}

export type RuntimeDataServiceInstance = RuntimeDataService<RuntimeDataServiceId>;
